import * as THREE from 'three';
import type { LightPreset } from '../types';

export type LightRig = {
  group: THREE.Group;
  hemi: THREE.HemisphereLight;
  key: THREE.DirectionalLight;
  fill: THREE.DirectionalLight;
  rim: THREE.DirectionalLight;
  spot: THREE.SpotLight;
  softboxLeft: THREE.RectAreaLight;
  softboxRight: THREE.RectAreaLight;
};

export function kelvinToColor(k: number): THREE.Color {
  // Tanner Helland
  const t = Math.max(1000, Math.min(40000, k)) / 100;
  let r: number;
  let g: number;
  let b: number;
  if (t <= 66) {
    r = 255;
    g = 99.4708025861 * Math.log(t) - 161.1195681661;
    b = t <= 19 ? 0 : 138.5177312231 * Math.log(t - 10) - 305.0447927307;
  } else {
    r = 329.698727446 * Math.pow(t - 60, -0.1332047592);
    g = 288.1221695283 * Math.pow(t - 60, -0.0755148492);
    b = 255;
  }
  return new THREE.Color(
    Math.max(0, Math.min(255, r)) / 255,
    Math.max(0, Math.min(255, g)) / 255,
    Math.max(0, Math.min(255, b)) / 255,
  );
}

function studioLightColor(hex: string, kelvin: number, mix = 0.4): THREE.Color {
  const base = new THREE.Color(hex);
  const tint = kelvinToColor(kelvin);
  return base.clone().lerp(tint, mix);
}

export function createLightRig(): LightRig {
  const group = new THREE.Group();
  group.name = 'light-rig';
  const hemi = new THREE.HemisphereLight(0xecf5ff, 0xd9d6cb, 1.2);
  group.add(hemi);

  const key = new THREE.DirectionalLight(0xffffff, 1.35);
  key.position.set(2500, 3600, 1800);
  key.castShadow = true;
  key.shadow.mapSize.set(4096, 4096); // 29.09: чёткие тени на больших сценах
  /* 29.09.2026 — теневая камера обязана покрывать сцену: дефолт Three.js
   * ±5 world units не накрывает квартиру 21000мм — тени просто не рисуются. */
  key.shadow.camera.left = -15000;
  key.shadow.camera.right = 15000;
  key.shadow.camera.top = 15000;
  key.shadow.camera.bottom = -15000;
  key.shadow.bias = 0.00012;
  key.shadow.normalBias = 8;
  key.shadow.radius = 6; // 29.09: мягкие премиум-тени
  key.shadow.camera.near = 300;
  key.shadow.camera.far = 11000;
  group.add(key);

  const fill = new THREE.DirectionalLight(0xf8fbff, 0.42);
  fill.position.set(-1800, 1700, 1600);
  group.add(fill);

  const rim = new THREE.DirectionalLight(0xa4cfff, 0.4);
  rim.position.set(-2600, 1400, -2000);
  group.add(rim);

  const spot = new THREE.SpotLight(0xffffff, 0.2, 18000, Math.PI / 5.2, 0.36, 1.25);
  spot.position.set(0, 4200, 500);
  spot.target.position.set(0, 1200, 0);
  group.add(spot.target);
  group.add(spot);

  const softboxLeft = new THREE.RectAreaLight('#fff4e8', 2.1, 1800, 1300);
  softboxLeft.position.set(-1700, 2100, 2100);
  softboxLeft.lookAt(0, 900, 0);
  group.add(softboxLeft);

  const softboxRight = new THREE.RectAreaLight('#eef7ff', 1.25, 1300, 1100);
  softboxRight.position.set(1900, 1600, 1500);
  softboxRight.lookAt(0, 700, 0);
  group.add(softboxRight);

  const rig: LightRig = { group, hemi, key, fill, rim, spot, softboxLeft, softboxRight };
  // Ссылка для aimSceneLightsAt: группа ≠ риг, а наводке света нужен сам риг.
  group.userData.lightRigRef = rig;
  return rig;
}

type PresetCfg = {
  bg: string;
  fog: string;
  exposure: number;
  hemi: number;
  key: number;
  fill: number;
  rim: number;
  spot: number;
  keyPos: [number, number, number];
  fillPos: [number, number, number];
  rimPos: [number, number, number];
  spotPos: [number, number, number];
  hemiSky: string;
  hemiGround: string;
};

// Tuned for product-photo look — darker, contrast, fall-off shadows.
// Studio is now an inspector mode (still light but balanced); Photo is showroom-grade.
const PRESETS: Record<LightPreset, PresetCfg> = {
  /* 05.10.2026 — калибровка контраста на белых кухнях (рендер-аудит кадра
   * квартиры: luma 228/255, контраст 45, dark% 0.1 — белое-на-белом без
   * объёма). Мебель РЕАЛЬНО белая (W1000 ST9), данные не трогаем — правим
   * плоский свет: hemi заметно ниже, key выше и контрастнее, rim ярче —
   * тени key-light обязаны читаться на белых фасадах. Туман чуть глубже,
   * но не в грязь: приказ владельца «квартиры не должны выглядеть пасмурно»
   * (экспозиция 0.92, а не 0.8). */
  studio: {
    bg: '#e8e6e0',
    fog: '#dcd9d1',
    exposure: 0.92,
    hemi: 0.48,
    key: 2.0,
    fill: 0.45,
    rim: 0.5,
    spot: 0.14,
    keyPos: [2200, 4200, 2600],
    fillPos: [-2000, 1800, 1400],
    rimPos: [-2800, 1600, -1800],
    spotPos: [0, 4600, 600],
    hemiSky: '#f4f2ee',
    hemiGround: '#c4bfb6',
  },
  showroom: {
    /* Тот же аудит: showroom-сцена на белом тоже мылилась. Контраст тем же
     * приёмом (hemi вниз, key вверх, rim вверх), туман на ступень глубже
     * студийного, но светлее photo — витрина, а не сумерки. */
    bg: '#8f99a5',
    fog: '#848e9a',
    exposure: 0.74,
    hemi: 0.26,
    key: 1.5,
    fill: 0.2,
    rim: 0.5,
    spot: 0.06,
    keyPos: [-1800, 3100, 2600],
    fillPos: [2300, 1600, 1300],
    rimPos: [1400, 1900, -2200],
    spotPos: [0, 3600, 400],
    hemiSky: '#c2ccd8',
    hemiGround: '#71695f',
  },
  daylight: {
    bg: '#8ca3ba',
    fog: '#73899e',
    exposure: 1.0,
    hemi: 0.72,
    key: 1.9,
    fill: 0.44,
    rim: 0.34,
    spot: 0.09,
    keyPos: [3400, 5200, 1800],
    fillPos: [-2100, 1800, 1300],
    rimPos: [-2900, 900, -1700],
    spotPos: [0, 3900, -400],
    hemiSky: '#cfe0ee',
    hemiGround: '#6e7b8a',
  },
  photo: {
    bg: '#7f8a97',
    fog: '#7f8a97',
    exposure: 0.78,
    hemi: 0.28,
    key: 1.26,
    fill: 0.22,
    rim: 0.48,
    spot: 0.1,
    keyPos: [1200, 2800, 3600],
    fillPos: [-1600, 1200, 1200],
    rimPos: [2600, 1600, -1300],
    spotPos: [0, 2900, 1100],
    hemiSky: '#bccada',
    hemiGround: '#675f56',
  },
};

/**
 * Geometry owns fog distance; lighting owns fog colour. Keeping those
 * responsibilities separate prevents a later light-preset effect from
 * restoring the small-scene 9-18m range over a fitted apartment camera.
 */
export function setSceneFogRange(scene: THREE.Scene, near: number, far: number): THREE.Fog {
  const safeNear = Math.max(0, Number.isFinite(near) ? near : 9000);
  const safeFar = Math.max(safeNear + 1, Number.isFinite(far) ? far : 18000);
  if (scene.fog instanceof THREE.Fog) {
    scene.fog.near = safeNear;
    scene.fog.far = safeFar;
    return scene.fog;
  }
  const color = scene.background instanceof THREE.Color
    ? scene.background.clone()
    : new THREE.Color('#8f99a5');
  const fog = new THREE.Fog(color, safeNear, safeFar);
  scene.fog = fog;
  return fog;
}

export function applyLightPreset(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  rig: LightRig,
  preset: LightPreset,
  lightTemperature = 5600,
) {
  const cfg = PRESETS[preset];
  /*
   * The area outside the architectural scene is a Viewer/UI concern, not a
   * lighting-preset concern.  Writing `scene.background` here made choosing
   * "Showroom" or "Photo" silently replace the curated DOM backdrop with a
   * dark WebGL box.  HDRI is the only explicit scene-background option and is
   * owned by `useHdri`; this function is deliberately limited to light and
   * atmospheric fog.
   */
  if (scene.fog instanceof THREE.Fog) {
    scene.fog.color.set(cfg.fog);
  } else {
    scene.fog = new THREE.Fog(cfg.fog, 9000, 18000);
  }
  renderer.toneMappingExposure = cfg.exposure;

  rig.hemi.intensity = cfg.hemi;
  rig.hemi.color = studioLightColor(cfg.hemiSky, lightTemperature, 0.3);
  rig.hemi.groundColor = studioLightColor(cfg.hemiGround, lightTemperature, 0.16);

  rig.key.intensity = cfg.key;
  rig.key.color = studioLightColor('#ffffff', lightTemperature, 0.42);
  rig.key.position.set(...cfg.keyPos);

  rig.fill.intensity = cfg.fill;
  rig.fill.color = studioLightColor('#f8fbff', lightTemperature, 0.36);
  rig.fill.position.set(...cfg.fillPos);

  rig.rim.intensity = cfg.rim;
  rig.rim.color = studioLightColor('#a4cfff', lightTemperature, 0.28);
  rig.rim.position.set(...cfg.rimPos);

  rig.spot.intensity = cfg.spot;
  rig.spot.position.set(...cfg.spotPos);

  rig.softboxLeft.intensity =
    preset === 'photo' ? 0.42 : preset === 'showroom' ? 0.34 : preset === 'daylight' ? 0.42 : 0.28;
  rig.softboxLeft.color = studioLightColor('#fff8ee', lightTemperature, 0.5);
  rig.softboxLeft.lookAt(0, 900, 0);

  rig.softboxRight.intensity =
    preset === 'photo' ? 0.28 : preset === 'showroom' ? 0.22 : preset === 'daylight' ? 0.3 : 0.18;
  rig.softboxRight.color = studioLightColor('#eef7ff', lightTemperature, 0.42);
  rig.softboxRight.lookAt(0, 720, 0);

  applyStoredLightAim(rig);
}

/* Свет из createLightRig целится в мировое (0,0,0), а теневая камера key-light
 * ограничена ±15000 вокруг цели. Сцена, чья геометрия лежит в стороне от нуля
 * (квартира 21м: x∈[8..29]м), целиком выпадает из теневой пирамиды — тени
 * просто не рисуются, и рендер читается плоским. aimSceneLightsAt наводит
 * key/spot на фактический центр сцены и расширяет теневую камеру под её
 * радиус; бокс запоминается, чтобы смена пресета (applyLightPreset) не
 * возвращала свет в ноль. */
let storedLightAim: { center: THREE.Vector3; radius: number } | null = null;

function applyStoredLightAim(rig: LightRig): void {
  if (!storedLightAim) return;
  const { center, radius } = storedLightAim;
  rig.key.target.position.copy(center);
  rig.key.position.copy(center).addScaledVector(AIM_DIRECTION, Math.max(9000, radius * 2.4));
  // MatrixWorld цели обновляется только в графе сцены — без родителя Three.js
  // держит тождественную матрицу, и свет светится из нуля.
  if (!rig.key.target.parent) rig.group.add(rig.key.target);
  const cam = rig.key.shadow.camera;
  const extent = Math.max(9000, Math.min(radius * 1.35, 42000));
  cam.left = -extent;
  cam.right = extent;
  cam.top = extent;
  cam.bottom = -extent;
  cam.near = 300;
  cam.far = Math.max(24000, radius * 6);
  cam.updateProjectionMatrix();
  rig.spot.target.position.copy(center);
  rig.spot.position.set(center.x, center.y + Math.max(4200, radius * 1.2), center.z + radius * 0.3);
}

const AIM_DIRECTION = new THREE.Vector3(0.42, 0.72, 0.5).normalize();

/**
 * Контактный AO обязан жить в масштабе сцены: жёсткие 16-24 «юнита»
 * дефолта SSAOPass в мм-мире квартиры — это 16 мм, невидимо вовсе
 * (рендер-аудит 03.10: кадр квартиры dark%=0). Хинт считается от
 * радиуса сцены, который уже посчитал aimSceneLightsAt.
 */
export function sceneAoRadiusHintMm(): number | null {
  if (!storedLightAim || !Number.isFinite(storedLightAim.radius) || storedLightAim.radius <= 0) return null;
  return Math.round(Math.max(60, Math.min(420, storedLightAim.radius * 0.045)));
}

export function aimSceneLightsAt(scene: THREE.Scene, box: THREE.Box3): void {
  if (box.isEmpty() || !Number.isFinite(box.min.x)) return;
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const radius = Math.max(size.x, size.y, size.z) * 0.5;
  if (!Number.isFinite(radius) || radius <= 0) return;
  storedLightAim = { center, radius };
  // Группа 'light-rig' — ЭТО THREE.Group, а не LightRig-объект: раньше её
  // кастовали вслепую, rig.key оказывался undefined, и панель «Свет и
  // отображение» валила сцену (TypeError reading 'target'). Настоящий риг
  // лежит в userData — его туда вешает createLightRig.
  const group = scene.getObjectByName('light-rig');
  const rigRef = group?.userData?.lightRigRef;
  if (rigRef) applyStoredLightAim(rigRef as LightRig);
}
